import { Grid } from '@mui/material';
import { ChangeEvent, FC, ReactElement, useContext, useEffect, useState } from 'react';
import { ReactComponent as UpcomingServiceIcon } from '../../../assets/images/clipboard-export-wrapper.svg';
import Button from '@mui/material/Button';
import InputText from '../../../components/form/InputText';
import TemplateBox from '../../form-templates/TemplateBox';
import { UscgSafetyContext } from '../../../context/uscgSafetyContext';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import InputDate from '../../../components/form/InputDate';
import { validateForm } from '../../../utils/form';
import { uscgServiceSchema } from './UscgServiceSchema';
import { getExpirationStateFromDate } from '../../../utils/lib';
import { useNavigate } from 'react-router-dom';
import paths from '../../../utils/constants/paths';

interface updateUscgSafetyDto {
  expiration_date: string;
  title: string;
  additional_information: string;
}

const updateUscgSafetyFormInitialState: updateUscgSafetyDto = {
  expiration_date: '',
  title: '',
  additional_information: ''
};

const updateUscgSafetyFormValidationErrorsInitialState: updateUscgSafetyDto = {
  expiration_date: '',
  title: '',
  additional_information: ''
};

const UscgSafetyForm: FC = (): ReactElement => {
  const {
    updateUscgSafety,
    listUscgSafety,
    state: uscgSafetyState
  } = useContext(UscgSafetyContext);
  const [form, setForm] = useState<updateUscgSafetyDto>(updateUscgSafetyFormInitialState);
  const [formValidationErrors, setFormValidationErrors] = useState<updateUscgSafetyDto>(
    updateUscgSafetyFormValidationErrorsInitialState
  );
  const location = useLocation();
  const { usgItem } = location.state;
  const navigate = useNavigate();

  const handleDateChange = (value: dayjs.Dayjs | null) => {
    setForm({
      ...form,
      expiration_date: value ? value.toISOString() : ''
    });
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmitForm = () => {
    const { errorSummary } = validateForm(uscgServiceSchema, form);

    setFormValidationErrors(errorSummary);

    if (!Object.values(errorSummary).length) {
      updateUscgSafety(usgItem.id, {
        expiration_date: form.expiration_date,
        title: form.title,
        additional_information: form.additional_information,
        vessel_id: usgItem.vessel_id,
        state: getExpirationStateFromDate(form.expiration_date)
      });

      listUscgSafety(usgItem.vessel_id);

      navigate({
        pathname: paths.VESSEL_USCG_SAFETY_LIST.absolutePath,
        search: `vesselId=${usgItem.vessel_id}`
      });
    }
  };

  useEffect(() => {
    if (usgItem) {
      setForm({
        expiration_date: usgItem.expiration_date,
        title: usgItem.title,
        additional_information: usgItem.additional_information
      });
    } else {
      setForm(updateUscgSafetyFormInitialState);
    }
  }, [usgItem]);

  return (
    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
      <Grid
        container
        display={'flex'}
        justifyContent={'space-between'}
        style={{ padding: '46px 0 27px 0', alignItems: 'center' }}>
        <Grid
          item
          xs={12}
          sm={6}
          md={6}
          lg={6}
          xl={6}
          style={{ display: 'flex', alignItems: 'center' }}>
          <UpcomingServiceIcon className="icon-title" />
          <p style={{ color: '#273F82', fontWeight: 'bold' }}>Edit USCG Safety</p>
        </Grid>

        <Grid item xs={12} sm={12} md={1} lg={1} xl={1.5} />
        <Grid item xs={12} sm={12} md={1} lg={1} xl={1.5} />
        <Grid item xs={12} sm={12} md={1} lg={1.5} xl={1.5}>
          <Button
            disabled={uscgSafetyState.update.loading}
            variant="outlined"
            type="button"
            onClick={handleSubmitForm}
            style={{ width: '100%', marginBottom: '20px' }}>
            Update
          </Button>
        </Grid>
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
        <TemplateBox minHeight={'calc(100vh - 240px)'}>
          <InputText
            name={'title'}
            label={'Title'}
            value={form.title}
            validationErrors={formValidationErrors.title}
            onChange={handleInputChange}
            width={'100%'}
          />
          <InputDate
            name={'expiration_date'}
            label="due date"
            calendarProps={{
              value: form.expiration_date,
              onChange: handleDateChange
            }}
            validationError={formValidationErrors.expiration_date || undefined}
          />
          <InputText
            name={'additional_information'}
            label={'Additional Information'}
            value={form.additional_information}
            validationErrors={formValidationErrors.additional_information}
            onChange={handleInputChange}
            width={'100%'}
          />
        </TemplateBox>
      </Grid>
    </Grid>
  );
};

export default UscgSafetyForm;
